import { NUMBER } from 'common/constants/gridType';
import { largeCurrencyFormat, oneDecimalPercentFormat, xStandardSuffixFormat } from 'common/formats/formats';
import { ChildCompany } from 'common/types/company';
import { Fund } from 'common/types/fund';
import { ConfigurationCell, ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import { AggregatedFundOwnershipDetail } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  WaterfallFundSummaryColumn,
  WaterfallFundSummaryTableData,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig.types';
import {
  DEFAULT_COL_FOR_KEY,
  HEADER_ALIAS_PREFIX,
  ROW_LABEL_CLASS,
  ROW_TOTAL_LABEL_CLASS,
  SUM_FUNCTION_NAME,
  TOTALS_EXIT_ALIAS,
  TOTALS_INVESTED_ALIAS,
  TOTALS_IRR_ALIAS,
  TOTALS_LABEL_ALIAS,
  TOTALS_LABEL_TEXT,
  TOTALS_LOAN_VALUE_ALIAS,
  TOTALS_MOIC_ALIAS,
  TOTALS_PREV_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/WaterfallFundSummaryParser/WaterfallFundSummaryParser.constants';
import {
  buildExitProceedsExpr,
  buildMoicExpr,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/WaterfallFundSummaryParser/WaterfallFundSummaryParser.util';
import { TABLE_HEADER_CLASS } from '../../constants';

export const waterfallFundSummaryParser = (
  props: ParserParams<WaterfallFundSummaryColumn, object, WaterfallFundSummaryTableData, AggregatedFundOwnershipDetail>
) => {
  const { rowConfig, tableData, columns } = props;

  const { captable } = tableData || {};
  if (!captable) {
    return {};
  }
  // build a list of fund ownership details from the captable where there is a related_entity

  const fundMap =
    tableData?.fundList?.reduce((acc: { [key: number]: Fund }, fund) => {
      if (fund.id) {
        acc[fund.id] = fund as Fund;
      }
      return acc;
    }, {}) || {};

  const childCompanyMap =
    tableData?.childrenCompanies?.reduce((acc: { [key: number]: ChildCompany }, company) => {
      if (company.id) {
        acc[company.id] = company;
      }
      return acc;
    }, {}) || {};

  const cells: { [key: string]: ConfigurationCell<AggregatedFundOwnershipDetail, object, object, object> } = {};

  const totalRows = rowConfig.length;

  // Helper to get column letter by index
  const colLetter = (i: number) => String.fromCharCode('A'.charCodeAt(0) + i);

  // Build a quick lookup of column letters by key
  const keyToLetter: Record<string, string> = columns.reduce((acc, c, i) => {
    acc[c.key] = colLetter(i);
    return acc;
  }, {});

  rowConfig.forEach((row, index) => {
    const rowNumber = index + 1;

    // Header row
    if (index === 0) {
      columns.forEach((c, i) => {
        const col = colLetter(i);
        const key = `${col}${rowNumber}`;
        cells[key] = {
          key,
          rowNumber,
          columnLegend: col,
          alias: `${HEADER_ALIAS_PREFIX}${c.key}`,
          value: c.label,
          readOnly: true,
          className: TABLE_HEADER_CLASS,
        };
      });
      return;
    }

    // Totals row
    if (index === totalRows - 1) {
      const firstDataRow = 2;
      // last data row should be the row before the totals row (exclude totals row itself)
      const lastDataRow = Math.max(firstDataRow, totalRows - 1);

      const entityCol = keyToLetter.entity ?? DEFAULT_COL_FOR_KEY.entity;
      const exitCol = keyToLetter.exitProceeds ?? DEFAULT_COL_FOR_KEY.exitProceeds;
      const prevCol = keyToLetter.previousProceeds ?? DEFAULT_COL_FOR_KEY.previousProceeds;
      const invCol = keyToLetter.investedCapital ?? DEFAULT_COL_FOR_KEY.investedCapital;
      const moicCol = keyToLetter.moic ?? DEFAULT_COL_FOR_KEY.moic;
      const irrCol = keyToLetter.irr ?? DEFAULT_COL_FOR_KEY.irr;

      const buildSum = (col: string) => {
        const refs: string[] = [];
        for (let r = firstDataRow; r <= lastDataRow; r++) {
          refs.push(`${col}${r}`);
        }
        if (refs.length === 0) {
          return '0';
        }
        return `=${SUM_FUNCTION_NAME}(${refs.join(',')})`;
      };

      // entity label cell
      const entityKey = `${entityCol}${rowNumber}`;
      cells[entityKey] = {
        key: entityKey,
        rowNumber,
        columnLegend: entityCol,
        alias: TOTALS_LABEL_ALIAS,
        value: TOTALS_LABEL_TEXT,
        readOnly: true,
        className: ROW_TOTAL_LABEL_CLASS,
      };

      // exit proceeds total
      const exitKey = `${exitCol}${rowNumber}`;
      cells[exitKey] = {
        key: exitKey,
        rowNumber,
        columnLegend: exitCol,
        alias: TOTALS_EXIT_ALIAS,
        readOnly: true,
        format: largeCurrencyFormat,
        gridType: NUMBER,
        expr: buildSum(exitCol),
        className: ROW_TOTAL_LABEL_CLASS,
      };

      // previous proceeds total
      const prevKey = `${prevCol}${rowNumber}`;
      cells[prevKey] = {
        key: prevKey,
        rowNumber,
        columnLegend: prevCol,
        alias: TOTALS_PREV_ALIAS,
        readOnly: true,
        format: largeCurrencyFormat,
        gridType: NUMBER,
        expr: buildSum(prevCol),
        className: ROW_TOTAL_LABEL_CLASS,
      };

      // invested capital total
      const invKey = `${invCol}${rowNumber}`;
      cells[invKey] = {
        key: invKey,
        rowNumber,
        columnLegend: invCol,
        alias: TOTALS_INVESTED_ALIAS,
        readOnly: true,
        format: largeCurrencyFormat,
        gridType: NUMBER,
        expr: buildSum(invCol),
        className: ROW_TOTAL_LABEL_CLASS,
      };

      // loan value total (only present when the column is included)
      const loanCol = keyToLetter.loanValue;
      if (loanCol) {
        const loanKey = `${loanCol}${rowNumber}`;
        cells[loanKey] = {
          key: loanKey,
          rowNumber,
          columnLegend: loanCol,
          alias: TOTALS_LOAN_VALUE_ALIAS,
          readOnly: true,
          format: largeCurrencyFormat,
          gridType: NUMBER,
          expr: buildSum(loanCol),
          className: ROW_TOTAL_LABEL_CLASS,
        };
      }

      // moic total formula using other totals cells
      const moicKey = `${moicCol}${rowNumber}`;
      cells[moicKey] = {
        key: moicKey,
        rowNumber,
        columnLegend: moicCol,
        alias: TOTALS_MOIC_ALIAS,
        readOnly: true,
        format: xStandardSuffixFormat,
        gridType: NUMBER,
        expr: buildMoicExpr(`${exitCol}${rowNumber}`, `${prevCol}${rowNumber}`, `${invCol}${rowNumber}`),
        className: ROW_TOTAL_LABEL_CLASS,
      };

      const irrKey = `${irrCol}${rowNumber}`;
      cells[irrKey] = {
        key: irrKey,
        columnLegend: irrCol,
        alias: TOTALS_IRR_ALIAS,
        readOnly: true,
        rowNumber,
        value: null,
        className: ROW_TOTAL_LABEL_CLASS,
      };

      return;
    }

    // loop through the fund ownership details and look for ones that have an entity reference in the child_fods map that matchup with the current row
    const fundId = row.data?.fund as number;

    // Data rows
    const entity = fundId ? fundMap[row.data?.fund as number] : childCompanyMap[row.data?.related_entity as number];

    const entityId = entity?.id ?? row.data?.fund ?? row.data?.related_entity;

    columns.forEach((column, i) => {
      const col = colLetter(i);
      const key = `${col}${rowNumber}`;

      switch (column.key) {
        case 'entity':
          cells[key] = {
            key,
            rowNumber,
            columnLegend: col,
            alias: column.key,
            value: entity?.name,
            readOnly: true,
            className: ROW_LABEL_CLASS,
          };
          break;
        case 'exitProceeds':
          if (entityId != null) {
            cells[key] = {
              key,
              rowNumber,
              columnLegend: col,
              alias: column.key,
              readOnly: true,
              format: largeCurrencyFormat,
              gridType: NUMBER,
              expr: buildExitProceedsExpr(entityId as number, Array.from(row.data?.related_reference_set ?? [])),
            };
          }
          break;
        case 'previousProceeds':
          if (row.data?.realized_value != null) {
            cells[key] = {
              key,
              rowNumber,
              columnLegend: col,
              alias: column.key,
              readOnly: true,
              format: largeCurrencyFormat,
              gridType: NUMBER,
              value: Number(row.data.realized_value),
            };
          }
          break;
        case 'investedCapital':
          if (row.data?.invested_capital != null) {
            cells[key] = {
              key,
              rowNumber,
              columnLegend: col,
              alias: column.key,
              readOnly: true,
              format: largeCurrencyFormat,
              gridType: NUMBER,
              value: Number(row.data.invested_capital),
            };
          }
          break;
        case 'loanValue':
          cells[key] = {
            key,
            rowNumber,
            columnLegend: col,
            alias: column.key,
            readOnly: true,
            format: largeCurrencyFormat,
            gridType: NUMBER,
            value: Number(row.data?.loan_value ?? '0'),
          };
          break;
        case 'moic': {
          const exitCol = keyToLetter.exitProceeds ?? DEFAULT_COL_FOR_KEY.exitProceeds;
          const prevCol = keyToLetter.previousProceeds ?? DEFAULT_COL_FOR_KEY.previousProceeds;
          const invCol = keyToLetter.investedCapital ?? DEFAULT_COL_FOR_KEY.investedCapital;
          cells[key] = {
            key,
            rowNumber,
            columnLegend: col,
            alias: column.key,
            readOnly: true,
            format: xStandardSuffixFormat,
            gridType: NUMBER,
            expr: buildMoicExpr(`${exitCol}${rowNumber}`, `${prevCol}${rowNumber}`, `${invCol}${rowNumber}`),
          };
          break;
        }
        case 'irr': {
          cells[key] = {
            key,
            rowNumber,
            columnLegend: col,
            alias: column.key,
            readOnly: true,
            format: oneDecimalPercentFormat,
            gridType: NUMBER,
            value: row.data?.irr ?? 0,
          };
          break;
        }
        default:
          break;
      }
    });
  });

  return cells;
};
